import groupBy from 'lodash/groupBy';
import flatMap from 'lodash/flatMap';
import findLast from 'lodash/findLast';

import {
  OrderDetail,
  RefundAvailability,
} from '../../components/receipt/transactions/TransactionDetailV2/TransactionDetailReceipt/OrderDetailType';

export interface Refund {
  categoryId: RefundAvailability['category_id'];
  categoryName: RefundAvailability['category_name'];
  discountPerItem: RefundAvailability['discount_per_item'];
  redemptionPerItem: RefundAvailability['redemption_per_item'];
  discounts: RefundAvailability['discounts'];
  itemId: RefundAvailability['item_id'];
  itemName: RefundAvailability['item_name'];
  itemImage: RefundAvailability['item_image'];
  itemVariantId: RefundAvailability['item_variant_id'];
  itemVariantName: RefundAvailability['item_variant_name'];
  itemVariantSku: RefundAvailability['item_variant_sku'];
  gratuityPerItem: RefundAvailability['gratuity_per_item'];
  grossPerItem: RefundAvailability['gross_per_item'];
  modifiers: RefundAvailability['modifiers'];
  gratuities: RefundAvailability['gratuities'];
  taxPerItem: RefundAvailability['tax_per_item'];
  trackStock: RefundAvailability['is_track_stock'];
  parentCheckoutId: RefundAvailability['parent_order_item_id'];
  parentCheckoutUuid: RefundAvailability['parent_order_item_uuid'];
  itemType: RefundAvailability['item_type'];
  bundleId: RefundAvailability['bundle_id'];
  pricePerItem: RefundAvailability['price_per_item'];
  netPerItem: RefundAvailability['net_per_item'];
  position: RefundAvailability['position'];
  salesTypeId: RefundAvailability['sales_type_id'];
  salesTypeName: RefundAvailability['sales_type_name'];
  maxQuantity: RefundAvailability['quantity'];
  isRefunded: boolean;
  refundAmount: number;
}

export function extractForRefundBreakdown(
  transactionDetail: OrderDetail
): Record<string, Refund[]> {
  // extract for refund breakdown
  const refundAvailabilities = transactionDetail.refund_availability;
  const refunds: Refund[] = (refundAvailabilities ?? []).map(
    (refundAvailability) => ({
      categoryId: refundAvailability.category_id,
      categoryName: refundAvailability.category_name,
      discountPerItem: refundAvailability.discount_per_item,
      redemptionPerItem: refundAvailability.redemption_per_item,
      discounts: refundAvailability.discounts,
      itemId: refundAvailability.item_id,
      itemName: refundAvailability.item_name,
      itemImage: refundAvailability.item_image,
      itemVariantId: refundAvailability.item_variant_id,
      itemVariantName: refundAvailability.item_variant_name,
      itemVariantSku: refundAvailability.item_variant_sku,
      gratuityPerItem: refundAvailability.gratuity_per_item,
      grossPerItem: refundAvailability.gross_per_item,
      modifiers: refundAvailability.modifiers,
      gratuities: refundAvailability.gratuities,
      taxPerItem: refundAvailability.tax_per_item,
      trackStock: refundAvailability.is_track_stock,
      parentCheckoutId: refundAvailability.parent_order_item_id,
      parentCheckoutUuid: refundAvailability.parent_order_item_uuid,
      itemType: refundAvailability.item_type,
      bundleId: refundAvailability.bundle_id,
      pricePerItem: refundAvailability.price_per_item,
      netPerItem: refundAvailability.net_per_item,
      position: refundAvailability.position,
      salesTypeId: refundAvailability.sales_type_id,
      salesTypeName: refundAvailability.sales_type_name,
      // max quantity is maximum amount that can be refunded
      maxQuantity: refundAvailability.quantity,
      // is refunded is whether the item is included as refunded item.
      // if 0, it can't be refunded and must be excluded
      isRefunded: refundAvailability.quantity! > 0,
      // default value is quantity if can be refunded
      refundAmount:
        refundAvailability.quantity! > 0 ? refundAvailability.quantity! : 0,
    })
  );
  return groupBy(refunds, (item) => item.salesTypeName);
}

export function constructRefundItemsPromo(
  transactionDetail: OrderDetail,
  refundItems: ReturnType<typeof extractForRefundBreakdown>
) {
  if (!transactionDetail.promos) {
    return;
  }

  const allRefundItems = flatMap(Object.values(refundItems));
  let isChecked = false;
  return transactionDetail.promos.map((promo) => {
    promo.items?.forEach((item) => {
      const lastMatchingRefund = findLast(
        allRefundItems,
        (refundItem) => refundItem.parentCheckoutUuid === item.order_item_uuid
      );
      if (lastMatchingRefund) {
        isChecked = lastMatchingRefund.isRefunded;
      }
    });

    return {
      ...promo,
      isChecked,
    };
  });
}
